import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";

const PWAInstallPrompt = () => {
  const location = useLocation();
  const isParentRoute = location?.pathname?.startsWith('/parent');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    setIsStandalone(isPWA);

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    // Skip prompt on localhost, if already in PWA mode, or on parent routes
    if (isPWA || window.location.hostname === 'localhost' || isParentRoute) return;

    // Handle Chrome/Android/PC prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);

      // Delay showing the prompt for better UX
      const hasShown = localStorage.getItem('pwa_prompt_shown');
      if (!hasShown) {
        setTimeout(() => setIsVisible(true), 10000); // 10 seconds after entry
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show after some time
    if (ios && !isPWA) {
      const hasShown = localStorage.getItem('pwa_prompt_shown');
      if (!hasShown) {
        setTimeout(() => setIsVisible(true), 10000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isParentRoute]);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    setIsVisible(false);
    installPrompt.prompt();

    const { outcome } = await installPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    setInstallPrompt(null);
    localStorage.setItem('pwa_prompt_shown', 'true');
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Persist rejection for the session or 24h
    localStorage.setItem('pwa_prompt_shown', 'true');
  };

  if (isStandalone || !isVisible || isParentRoute) return null;

  return (
    <Dialog open={isVisible} onOpenChange={setIsVisible}>
      <DialogContent className="sm:max-w-md bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 bg-[hsl(var(--green-bright))]/10 rounded-2xl flex items-center justify-center mb-4">
            <Download className="w-8 h-8 text-[hsl(var(--green-bright))]" />
          </div>
          <DialogTitle className="text-center text-xl font-bold budlee-brand-logo !text-xl !text-transparent bg-clip-text leading-tight">
            Install Budlee AI
          </DialogTitle>
          <DialogDescription className="text-center text-[hsl(var(--muted-foreground))]">
            Get the full learning experience. Fast, accessible, and works even when you're offline!
          </DialogDescription>
        </DialogHeader>

        {isIOS ? (
          <div className="space-y-4 py-4">
            <div className="bg-[hsl(var(--muted))]/30 p-4 rounded-xl space-y-3 border border-[hsl(var(--card-border))]">
              <p className="text-sm font-semibold text-[hsl(var(--foreground))] flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[hsl(var(--green-bright))] text-white rounded-full flex items-center justify-center text-xs">1</span>
                Tap the Share button <Share className="w-4 h-4 text-blue-500" /> in Safari.
              </p>
              <p className="text-sm font-semibold text-[hsl(var(--foreground))] flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[hsl(var(--green-bright))] text-white rounded-full flex items-center justify-center text-xs">2</span>
                Scroll and tap "Add to Home Screen" <PlusSquare className="w-4 h-4 text-blue-500" />.
              </p>
            </div>
          </div>
        ) : (
          <div className="py-4 flex flex-col items-center justify-center">
            <ul className="text-sm text-[hsl(var(--muted-foreground))] space-y-2">
              <li className="flex items-center gap-2 font-medium">✨ Instant access from home screen</li>
              <li className="flex items-center gap-2 font-medium">🚀 Faster performance & loading</li>
              <li className="flex items-center gap-3 font-medium">📖 Offline reading support</li>
            </ul>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
          {!isIOS && (
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:opacity-90 font-bold h-11"
            >
              Install App
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="flex-1 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 font-semibold h-11"
          >
            {isIOS ? 'Got it!' : 'Maybe Later'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PWAInstallPrompt;
